import { apiClient, handleAPIResponse } from '../client.js';

/**
 * Base Service Class for Laravel API Operations
 * Similar to Base44 SDK entity operations
 */
export class BaseService {
  constructor(entityName) {
    this.entityName = entityName;
    this.client = apiClient;
  }

  /**
   * Create a new record
   */
  async create(data) {
    const response = await handleAPIResponse(
      this.client.post(`/api/${this.entityName}`, data)
    );
    return response.data || response.error;
  }

  /**
   * Get all records with optional pagination and filters
   */
  async list(page = 1, limit = 50, filters = {}) {
    const params = {
      page,
      limit,
      ...filters
    };

    const response = await handleAPIResponse(
      this.client.get(`/api/${this.entityName}`, params)
    );
    
    // Handle error case
    if (response.error) {
      console.error(`Error loading ${this.entityName}:`, response.error);
      return [];
    }
    
    // Extract data from Laravel API response structure
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data; // Return the actual data array
    }
    
    // If response.data is already an array, return it
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // Fallback to empty array if structure is unexpected
    console.warn(`Unexpected response structure for ${this.entityName}:`, response.data);
    return [];
  }

  /**
   * Get a specific record by ID
   */
  async getById(id) {
    const response = await handleAPIResponse(
      this.client.get(`/api/${this.entityName}/${id}`)
    );
    return response.data || response.error;
  }

  /**
   * Update a record by ID
   */
  async update(id, data) {
    const response = await handleAPIResponse(
      this.client.put(`/api/${this.entityName}/${id}`, data)
    );
    return response.data || response.error;
  }

  /**
   * Partially update a record by ID
   */
  async patch(id, data) {
    const response = await handleAPIResponse(
      this.client.patch(`/api/${this.entityName}/${id}`, data)
    );
    return response.data || response.error;
  }

  /**
   * Delete a record by ID
   */
  async delete(id) {
    const response = await handleAPIResponse(
      this.client.delete(`/api/${this.entityName}/${id}`)
    );
    return response.data || response.error;
  }

  /**
   * Search records with query
   */
  async search(query, filters = {}) {
    const params = {
      q: query,
      ...filters
    };

    const response = await handleAPIResponse(
      this.client.get(`/api/${this.entityName}/search`, params)
    );
    
    // Handle error case
    if (response.error) {
      console.error(`Error searching ${this.entityName}:`, response.error);
      return [];
    }
    
    // Extract data from Laravel API response structure
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data; // Return the actual data array
    }
    
    // If response.data is already an array, return it
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // Fallback to empty array if structure is unexpected
    console.warn(`Unexpected search response structure for ${this.entityName}:`, response.data);
    return [];
  }

  /**
   * Bulk create multiple records
   */
  async bulkCreate(records) {
    const response = await handleAPIResponse(
      this.client.post(`/api/${this.entityName}/bulk`, { records })
    );
    return response.data || response.error;
  }

  /**
   * Bulk update multiple records
   */
  async bulkUpdate(updates) {
    const response = await handleAPIResponse(
      this.client.patch(`/api/${this.entityName}/bulk`, { updates })
    );
    return response.data || response.error;
  }

  /**
   * Get records with specific fields only
   */
  async select(fields = [], filters = {}) {
    const params = {
      fields: fields.join(','),
      ...filters
    };

    const response = await handleAPIResponse(
      this.client.get(`/api/${this.entityName}`, params)
    );
    return response.data || response.error;
  }

  /**
   * Count total records with optional filters
   */
  async count(filters = {}) {
    const response = await handleAPIResponse(
      this.client.get(`/api/${this.entityName}/count`, filters)
    );
    return response.data || response.error;
  }

  /**
   * Upload file for this entity
   */
  async uploadFile(file, recordId = null, metadata = {}) {
    const additionalData = recordId 
      ? { record_id: recordId, ...metadata }
      : metadata;

    const response = await handleAPIResponse(
      this.client.upload(`/api/${this.entityName}/upload`, file, additionalData)
    );
    return response.data || response.error;
  }

  /**
   * Get records with pagination info
   */
  async paginate(page = 1, perPage = 15, filters = {}) {
    const params = {
      page,
      per_page: perPage,
      ...filters
    };

    const response = await handleAPIResponse(
      this.client.get(`/api/${this.entityName}`, params)
    );
    
    // For pagination, we want to keep the full response structure
    return response.data || response.error;
  }

  /**
   * Custom API call for entity-specific endpoints
   */
  async customCall(endpoint, method = 'GET', data = {}) {
    const fullEndpoint = `/api/${this.entityName}/${endpoint}`;
    
    let response;
    switch (method.toUpperCase()) {
      case 'POST':
        response = await handleAPIResponse(this.client.post(fullEndpoint, data));
        break;
      case 'PUT':
        response = await handleAPIResponse(this.client.put(fullEndpoint, data));
        break;
      case 'PATCH':
        response = await handleAPIResponse(this.client.patch(fullEndpoint, data));
        break;
      case 'DELETE':
        response = await handleAPIResponse(this.client.delete(fullEndpoint));
        break;
      default:
        response = await handleAPIResponse(this.client.get(fullEndpoint, data));
    }
    
    return response.data || response.error;
  }

  /**
   * Get entity schema/structure
   */
  async getSchema() {
    const response = await handleAPIResponse(
      this.client.get(`/api/${this.entityName}/schema`)
    );
    return response.data || response.error;
  }
}

export default BaseService; 